import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Info, Loader2, Store, Truck } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { fmtGNF, PAYMENT_METHODS } from "@/lib/format";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [zones, setZones] = useState([]);
  const [form, setForm] = useState({
    customer_name: user?.name || "",
    phone: user?.phone || "",
    address: user?.address || "",
    zone_id: "",
    delivery_mode: "delivery",
    pickup_date: "",
    pickup_slot: "",
    payment_method: "orange_money",
    payment_phone: "",
    payment_reference: "",
    note: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [placed, setPlaced] = useState(null);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [payInfo, setPayInfo] = useState(null);
  const [pickup, setPickup] = useState({ slots: [], days: [], opening: "" });
  const hasAdult = items.some((i) => i.is_adult);

  useEffect(() => {
    api.get("/delivery-zones").then((r) => {
      setZones(r.data);
      setForm((f) => ({ ...f, zone_id: f.zone_id || r.data[0]?.id || "" }));
    });
    api.get("/payment-info").then((r) => setPayInfo(r.data)).catch(() => {});
    api.get("/pickup-slots").then((r) => {
      setPickup(r.data);
      setForm((f) => ({
        ...f,
        pickup_date: f.pickup_date || r.data.days?.[0]?.date || "",
        pickup_slot: f.pickup_slot || r.data.slots?.[0] || "",
      }));
    }).catch(() => {});
  }, []);

  const zone = useMemo(() => zones.find((z) => z.id === form.zone_id), [zones, form.zone_id]);
  const freeAbove = Number(zone?.free_above || 0);
  const freeDelivery = Boolean(freeAbove && subtotal >= freeAbove);
  const deliveryFee = form.delivery_mode === "pickup" || freeDelivery ? 0 : Number(zone?.fee || 0);
  const total = subtotal + deliveryFee;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/orders", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        age_confirmed: ageConfirmed,
        ...form,
      });
      clear();
      setPlaced(data);
      toast.success(`Commande ${data.number} enregistrée`);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (placed)
    return (
      <div className="mx-auto max-w-xl px-4 py-14 text-center ds-rise" data-testid="order-success">
        <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-600" />
        <h1 className="mt-4 font-display text-2xl font-bold text-slate-900">Commande enregistrée !</h1>
        <p className="font-num mt-2 text-sm text-slate-600">
          Numéro : <span className="font-bold text-slate-900">{placed.number}</span>
        </p>
        <div className="ds-card mt-5 space-y-2 p-4 text-left text-sm">
          <Row label="Total à payer" value={fmtGNF(placed.total)} bold />
          <Row label="Paiement" value={placed.payment_method_label} />
          <Row label="Statut" value={placed.status} />
          <Row label="Livraison" value={`${placed.zone_name} · ${placed.zone_eta || ""}`} />
          {placed.delivery_mode === "pickup" && placed.pickup_date && (
            <Row label="Retrait prévu" value={`${placed.pickup_date} · ${placed.pickup_slot}`} />
          )}
          <Row label="Téléphone" value={placed.phone} />
        </div>
        {placed.payment_method !== "cash_on_delivery" && (
          <p className="mt-4 rounded-xl bg-amber-50 px-3 py-2 text-left text-xs text-amber-800">
            Votre paiement {placed.payment_method_label} sera vérifié par EMPIRE-SHOP.
            La préparation du colis démarre dès la confirmation de l'encaissement.
          </p>
        )}
        <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-center">
          <Link to="/commandes" className="ds-btn-primary" data-testid="success-see-orders">Suivre ma commande</Link>
          <Link to="/catalogue" className="ds-btn-ghost">Continuer mes achats</Link>
        </div>
      </div>
    );

  if (items.length === 0)
    return (
      <div className="mx-auto max-w-lg px-4 py-20 text-center">
        <p className="font-display text-xl font-bold text-slate-900">Votre panier est vide</p>
        <Link to="/catalogue" className="ds-btn-primary mt-4">Voir le catalogue</Link>
      </div>
    );

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6" data-testid="checkout-page">
      <h1 className="ds-h1">Finaliser ma commande</h1>

      <form onSubmit={submit} className="mt-5 grid gap-5 lg:grid-cols-[1.6fr_1fr]">
        <div className="space-y-4">
          <section className="ds-card p-4">
            <h2 className="font-display text-base font-bold text-slate-900">Coordonnées</h2>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <Field label="Nom complet" required value={form.customer_name} onChange={(v) => setForm({ ...form, customer_name: v })} testid="checkout-name" />
              <Field label="Téléphone" required value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} placeholder="+224 6XX XX XX XX" testid="checkout-phone" />
            </div>
            <div className="mt-3">
              <label className="ds-label">Adresse de livraison *</label>
              <textarea
                required
                rows={2}
                className="ds-input mt-1"
                placeholder="Quartier, commune, repère proche"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                data-testid="checkout-address"
              />
            </div>
          </section>

          <section className="ds-card p-4">
            <h2 className="font-display text-base font-bold text-slate-900">Mode de livraison</h2>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {[
                { id: "delivery", label: "Livraison à domicile", icon: Truck, hint: "Selon la zone choisie" },
                { id: "pickup", label: "Retrait à l'entrepôt", icon: Store, hint: "Gratuit · choisissez votre créneau" },
              ].map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setForm({ ...form, delivery_mode: m.id })}
                  className={`flex items-start gap-3 rounded-xl border p-3 text-left transition-colors ${
                    form.delivery_mode === m.id ? "border-brand bg-brand-light" : "border-slate-300 bg-white"
                  }`}
                  data-testid={`delivery-mode-${m.id}`}
                >
                  <m.icon className="mt-0.5 h-5 w-5 text-brand" />
                  <span>
                    <span className="block text-sm font-semibold text-slate-900">{m.label}</span>
                    <span className="block text-xs text-slate-500">{m.hint}</span>
                  </span>
                </button>
              ))}
            </div>

            {form.delivery_mode === "pickup" && (
              <div className="mt-4 rounded-xl border border-brand/30 bg-brand-light/60 p-3" data-testid="pickup-slot-block">
                <p className="text-xs font-semibold text-brand">Créneau de retrait à l'entrepôt</p>
                <p className="mt-0.5 text-[11px] text-slate-600">{pickup.opening}</p>
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  <div>
                    <label className="ds-label">Jour de retrait *</label>
                    <select
                      required
                      value={form.pickup_date}
                      onChange={(e) => setForm({ ...form, pickup_date: e.target.value })}
                      className="ds-input mt-1"
                      data-testid="checkout-pickup-date"
                    >
                      {pickup.days.map((d) => (
                        <option key={d.date} value={d.date}>{d.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="ds-label">Heure de retrait *</label>
                    <select
                      required
                      value={form.pickup_slot}
                      onChange={(e) => setForm({ ...form, pickup_slot: e.target.value })}
                      className="ds-input mt-1"
                      data-testid="checkout-pickup-slot"
                    >
                      {pickup.slots.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            <label className="ds-label mt-4 block">Zone de livraison *</label>            <select
              required
              value={form.zone_id}
              onChange={(e) => setForm({ ...form, zone_id: e.target.value })}
              className="ds-input mt-1"
              data-testid="checkout-zone-select"
            >
              {zones.map((z) => (
                <option key={z.id} value={z.id}>
                  {z.name} — {z.fee > 0 ? fmtGNF(z.fee) : "Gratuit"} ({z.eta})
                </option>
              ))}
            </select>
            {freeAbove > 0 && (
              <p
                className={`mt-2 rounded-xl px-3 py-2 text-xs ${
                  freeDelivery ? "bg-emerald-50 text-emerald-800" : "bg-brand-light text-brand"
                }`}
                data-testid="checkout-free-delivery"
              >
                {freeDelivery
                  ? `Livraison offerte : votre commande dépasse ${fmtGNF(freeAbove)}.`
                  : `Livraison offerte dès ${fmtGNF(freeAbove)} d'achat — il vous manque ${fmtGNF(freeAbove - subtotal)}.`}
              </p>
            )}
          </section>

          <section className="ds-card p-4">
            <h2 className="font-display text-base font-bold text-slate-900">Paiement avant livraison</h2>
            <p className="mt-1 text-xs text-slate-500">
              Le règlement se fait au moment de la commande. Votre colis est préparé dès que EMPIRE-SHOP confirme l'encaissement.
            </p>
            <div className="mt-3 space-y-2">
              {PAYMENT_METHODS.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setForm({ ...form, payment_method: m.id })}
                  className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors ${
                    form.payment_method === m.id ? "border-brand bg-brand-light" : "border-slate-300 bg-white"
                  }`}
                  data-testid={`payment-method-${m.id}`}
                >
                  <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg border text-[10px] font-bold ${m.color}`}>
                    {m.id === "orange_money" ? "OM" : m.id === "mtn_momo" ? "MTN" : "CASH"}
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-slate-900">{m.label}</span>
                    <span className="block text-xs text-slate-500">{m.hint}</span>
                  </span>
                </button>
              ))}
            </div>

            {payInfo && (
              <div className="mt-3 rounded-xl border border-brand/30 bg-brand-light p-3 text-xs text-slate-700" data-testid="payment-instructions">
                {form.payment_method === "orange_money" && payInfo.orange_number && (
                  <p className="font-num text-sm font-bold text-brand">
                    Orange Money : {payInfo.orange_number} {payInfo.orange_name ? `(${payInfo.orange_name})` : ""}
                  </p>
                )}
                {form.payment_method === "mtn_momo" && payInfo.mtn_number && (
                  <p className="font-num text-sm font-bold text-brand">
                    MTN MoMo : {payInfo.mtn_number} {payInfo.mtn_name ? `(${payInfo.mtn_name})` : ""}
                  </p>
                )}
                {form.payment_method === "bank_transfer" && payInfo.bank_account && (
                  <p className="font-num text-sm font-bold text-brand">
                    {payInfo.bank_name} — compte {payInfo.bank_account}
                  </p>
                )}
                <p className="mt-1">
                  Montant à régler : <strong className="font-num">{fmtGNF(total)}</strong>
                </p>
                <p className="mt-1">{payInfo.instructions}</p>
              </div>
            )}

            <Field
              label={form.payment_method === "bank_transfer" ? "Numéro du bordereau / compte émetteur" : "Numéro Mobile Money utilisé"}
              required
              value={form.payment_phone}
              onChange={(v) => setForm({ ...form, payment_phone: v })}
              placeholder={form.payment_method === "bank_transfer" ? "N° de compte ou agence" : "+224 6XX XX XX XX"}
              testid="checkout-payment-phone"
              className="mt-3"
            />
            <Field
              label="Référence de la transaction"
              required
              value={form.payment_reference}
              onChange={(v) => setForm({ ...form, payment_reference: v })}
              placeholder="Ex. OM240931ABC ou n° de reçu"
              testid="checkout-payment-reference"
              className="mt-3"
            />
            <p className="mt-3 flex gap-2 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="payment-disclaimer">
              <Info className="h-4 w-4 shrink-0" />
              Votre paiement est vérifié manuellement par EMPIRE-SHOP (API opérateur pas encore branchée).
              La commande passe en préparation uniquement après confirmation de l'encaissement.
            </p>

            <div className="mt-3">
              <label className="ds-label">Note pour le livreur (optionnel)</label>
              <textarea
                rows={2}
                className="ds-input mt-1"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                data-testid="checkout-note"
              />
            </div>
          </section>
        </div>

        <div className="ds-card h-fit p-4 lg:sticky lg:top-24">
          <h2 className="font-display text-lg font-bold text-slate-900">Ma commande</h2>
          <div className="mt-3 max-h-56 space-y-2 overflow-y-auto pr-1 text-sm">
            {items.map((i) => (
              <div key={i.product_id} className="flex justify-between gap-2">
                <span className="line-clamp-2 text-slate-600">
                  {i.quantity} × {i.name}
                </span>
                <span className="font-num shrink-0 font-semibold">{fmtGNF(i.total)}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 space-y-2 border-t border-slate-200 pt-3 text-sm">
            <Row label="Sous-total" value={fmtGNF(subtotal)} />
            <Row label="Livraison" value={fmtGNF(deliveryFee)} />
            <div className="flex items-center justify-between border-t border-slate-200 pt-2">
              <span className="font-display font-bold text-slate-900">Total</span>
              <span className="font-num text-lg font-black text-brand" data-testid="checkout-total">{fmtGNF(total)}</span>
            </div>
          </div>
          {error && (
            <p className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="checkout-error">
              {error}
            </p>
          )}
          {hasAdult && (
            <label className="mt-4 flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="age-confirm-block">
              <input
                type="checkbox"
                required
                checked={ageConfirmed}
                onChange={(e) => setAgeConfirmed(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-brand"
                data-testid="age-confirm-checkbox"
              />
              <span>
                Votre panier contient des produits réservés aux adultes (chicha, cigarettes électroniques).
                <strong> Je certifie avoir 18 ans ou plus.</strong> Une pièce d'identité peut être demandée à la livraison.
              </span>
            </label>
          )}
          <p className="mt-3 text-xs text-slate-500" data-testid="checkout-payment-deadline">
            Réglez votre commande sous 48 h : passé ce délai, elle est annulée automatiquement et le stock libéré.
          </p>

          <button type="submit" disabled={busy || (hasAdult && !ageConfirmed)} className="ds-btn-primary mt-4 w-full" data-testid="checkout-submit-button">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Confirmer la commande
          </button>
        </div>
      </form>
    </div>
  );
}

const Field = ({ label, value, onChange, required, placeholder, testid, className = "" }) => (
  <div className={className}>
    <label className="ds-label">
      {label} {required && "*"}
    </label>
    <input
      required={required}
      className="ds-input mt-1"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
    />
  </div>
);

const Row = ({ label, value, bold }) => (
  <div className="flex items-center justify-between gap-3">
    <span className="text-slate-600">{label}</span>
    <span className={`font-num ${bold ? "font-bold text-slate-900" : "font-semibold"}`}>{value}</span>
  </div>
);
